import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarHalfRoundedIcon from '@mui/icons-material/StarHalfRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';

export interface StarRatingProps {
  /** Value between 0 and 5 */
  value: number;
  /** Total number of reviews / votes */
  count?: number;
  /** Icon size in px */
  size?: 'small' | 'medium' | 'large';
  /** Show numeric value next to stars */
  showValue?: boolean;
  /** Show review count */
  showCount?: boolean;
  /** Interactive – allow user to click */
  interactive?: boolean;
  onChange?: (value: number) => void;
}

const SIZE_MAP = { small: 16, medium: 20, large: 24 };

export const StarRating: React.FC<StarRatingProps> = ({
  value,
  count,
  size = 'medium',
  showValue = false,
  showCount = true,
  interactive = false,
  onChange,
}) => {
  const [hovered, setHovered] = React.useState<number | null>(null);
  const px = SIZE_MAP[size];
  const display = hovered ?? value;

  const getIcon = (index: number) => {
    const filled = display >= index;
    const half = !filled && display >= index - 0.5;
    const iconSx = { fontSize: px, color: '#ffd93d' };

    if (filled) return <StarRoundedIcon key={index} sx={iconSx} />;
    if (half) return <StarHalfRoundedIcon key={index} sx={iconSx} />;
    return <StarOutlineRoundedIcon key={index} sx={{ ...iconSx, color: '#d9d9d9' }} />;
  };

  return (
    <Box display="flex" alignItems="center" gap={0.25}>
      <Box display="flex" alignItems="center" sx={{ cursor: interactive ? 'pointer' : 'default' }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Box
            key={i}
            onMouseEnter={() => interactive && setHovered(i)}
            onMouseLeave={() => interactive && setHovered(null)}
            onClick={() => interactive && onChange?.(i)}
            sx={{ lineHeight: 0 }}
          >
            {getIcon(i)}
          </Box>
        ))}
      </Box>

      {showValue && (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: 'text.primary', ml: 0.5, lineHeight: 1 }}
        >
          {value.toFixed(1)}
        </Typography>
      )}

      {showCount && count !== undefined && (
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.25 }}>
          ({count.toLocaleString()})
        </Typography>
      )}
    </Box>
  );
};

export default StarRating;
